import clsx from "clsx";
import type { ReactNode } from "react";

/** Points de suspension finaux d'un libellé (« … » ou « ... »), espaces compris. */
const TRAILING_ELLIPSIS = /\s*(?:…|\.\.\.)\s*$/;

/** Libellé sans ses points de suspension finaux, ou `null` s'il n'en a pas. */
export function waitingBase(text: string): string | null {
  return TRAILING_ELLIPSIS.test(text) ? text.replace(TRAILING_ELLIPSIS, "") : null;
}

/** Libellé d'attente (R13) : « Génération en cours… », « Chargement… », « Enregistrement… ». Les points de
 *  suspension finaux ondulent doucement en boucle (trois points, 1,2 s par cycle, décalés de 0,15 s) pour faire
 *  patienter pendant une action ou un chargement ; leur largeur est réservée, si bien que rien ne bouge autour.
 *
 *  - Le texte passé est le libellé complet, tel que rendu par `t()` (« Génération en cours… ») : la clé de
 *    traduction ne change pas, le composant retire lui-même les points finaux pour les animer.
 *  - Lecteurs d'écran : le libellé complet est lu (texte masqué à l'écran) ; la partie visible et les points
 *    animés sont `aria-hidden`.
 *  - Mouvement réduit : « … » fixe, sans animation (index.css, `.gx-waiting`).
 *  - Un libellé sans points de suspension finaux est rendu tel quel.
 *
 *  Contrôle : `ui-check` (règle n) signale un libellé d'attente « …» affiché hors de ce composant. Ne pas l'employer
 *  pour une entrée de menu qui ouvre une fenêtre (« Partager… ») ni pour un `placeholder`. */
export function Waiting({ children, className }: { children: ReactNode; className?: string }) {
  if (typeof children !== "string") return <>{children}</>;
  const base = waitingBase(children);
  if (base === null) return <>{children}</>;
  return (
    <span className={clsx("gx-waiting", className)}>
      <span className="sr-only">{children}</span>
      <span aria-hidden="true">
        {base}
        <span className="gx-waiting-dots">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
        <span className="gx-waiting-still">…</span>
      </span>
    </span>
  );
}
